import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HiOutlineLightBulb, HiOutlineCamera } from 'react-icons/hi';
import { blogService, categoriesService } from '../services/api';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import ImageWithSize from '../components/editorjs/ImageWithSize';
import Quote from '@editorjs/quote';
import Code from '@editorjs/code';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import LinkTool from '@editorjs/link';
import Delimiter from '@editorjs/delimiter';
import RawTool from '@editorjs/raw';
import InlineCode from '@editorjs/inline-code';
import Underline from '@editorjs/underline';
import Marker from '@editorjs/marker';
import CheckList from '@editorjs/checklist';
import Warning from '@editorjs/warning';
import AttachesTool from '@editorjs/attaches';
import Button from 'editorjs-button';
import DragDrop from 'editorjs-drag-drop';
import Undo from 'editorjs-undo';
import LayoutColumns from '../components/editorjs/LayoutColumns';
import './BlogEditorJS.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const MEDIA_URL = API_URL.replace(/\/api$/, '');

const BlogEditorJS = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [additionalImages, setAdditionalImages] = useState([]);
    const [categories, setCategories] = useState([]);
    
    const [formData, setFormData] = useState({
        title_es: '',
        slug: '',
        content_es: '',
        excerpt_es: '',
        category_id: '',
        featured_image: null,
        gallery_images: [],
        existing_gallery_images: [],
        status: 'draft',
        published_at: '',
        scheduled_publish_date: '',
        scheduled_publish_time: ''
    });

    // Inicializar Editor.js
    useEffect(() => {
        let timeoutId;
        
        const initEditor = () => {
            const holder = document.getElementById('editorjs');
            if (!holder) {
                timeoutId = setTimeout(initEditor, 50);
                return;
            }

            if (editorRef.current) return; // Ya inicializado

            try {
                editorRef.current = new EditorJS({
                    holder: 'editorjs',
                    placeholder: 'Haz clic aquí para escribir...',
                    minHeight: 400,
                    
                    tools: {
                        // Bloques básicos
                        header: { 
                            class: Header, 
                            inlineToolbar: true,
                            config: {
                                levels: [1, 2, 3, 4, 5, 6],
                                defaultLevel: 2
                            }
                        },
                        paragraph: { 
                            class: Paragraph, 
                            inlineToolbar: true
                        },
                        
                        // Listas
                        list: { 
                            class: List, 
                            inlineToolbar: true
                        },
                        checklist: {
                            class: CheckList,
                            inlineToolbar: true
                        },
                        
                        // Imágenes con control de tamaño
                        image: {
                            class: ImageWithSize,
                            config: {
                                uploader: {
                                    async uploadByFile(file) {
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        try {
                                            const res = await fetch(`${API_URL}/upload/image`, {
                                                method: 'POST',
                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                                body: fd
                                            });
                                            
                                            if (!res.ok) {
                                                console.error('Upload failed:', res.status);
                                                return { success: 0 };
                                            }
                                            
                                            const data = await res.json();
                                            console.log('Upload success:', data);
                                            
                                            return { 
                                                success: 1, 
                                                file: { 
                                                    url: `${MEDIA_URL}${data.url}` 
                                                } 
                                            };
                                        } catch (error) {
                                            console.error('Upload error:', error);
                                            return { success: 0 };
                                        }
                                    },
                                    
                                    // Método necesario para cargar imágenes existentes al editar
                                    async uploadByUrl(url) {
                                        console.log('📥 Cargando imagen existente:', url);
                                        return {
                                            success: 1,
                                            file: {
                                                url: url
                                            }
                                        };
                                    }
                                },
                                actions: [
                                    {
                                        name: 'stretched',
                                        icon: '<svg width="17" height="10"><path d="M1 1h15M1 9h15"/></svg>',
                                        title: 'Ancho completo',
                                    },
                                ],
                            }
                        },
                        
                        // Texto y formato
                        quote: { 
                            class: Quote, 
                            inlineToolbar: true
                        },
                        warning: {
                            class: Warning,
                            inlineToolbar: true,
                            config: {
                                titlePlaceholder: 'Título...',
                                messagePlaceholder: 'Mensaje...'
                            }
                        },
                        
                        // Botones y call-to-actions
                        button: {
                            class: Button,
                            inlineToolbar: false,
                            config: {
                                css: {
                                    btnColor: "btn--primary",
                                }
                            }
                        },
                        
                        // Layout personalizado - COLUMNAS
                        columns: {
                            class: LayoutColumns,
                            inlineToolbar: false
                        },
                        
                        // Código y embeds
                        code: Code,
                        embed: { class: Embed, inlineToolbar: true },
                        
                        // Tablas y separadores
                        table: { 
                            class: Table, 
                            inlineToolbar: true,
                            config: {
                                rows: 2,
                                cols: 3,
                            }
                        },
                        delimiter: Delimiter,
                        
                        // Archivos adjuntos
                        attaches: {
                            class: AttachesTool,
                            config: {
                                uploader: {
                                    async uploadByFile(file) {
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        try {
                                            const res = await fetch(`${API_URL}/upload/document`, {
                                                method: 'POST',
                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                                body: fd
                                            });
                                            
                                            if (!res.ok) {
                                                return { success: 0 };
                                            }
                                            
                                            const data = await res.json();
                                            return { 
                                                success: 1, 
                                                file: { 
                                                    url: `${MEDIA_URL}${data.url}`,
                                                    size: file.size,
                                                    name: file.name,
                                                    extension: file.name.split('.').pop()
                                                } 
                                            };
                                        } catch (error) {
                                            return { success: 0 };
                                        }
                                    }
                                }
                            }
                        },
                        
                        // Links y HTML
                        linkTool: LinkTool,
                        raw: RawTool,
                        
                        // Herramientas inline
                        underline: Underline,
                        inlineCode: InlineCode,
                        marker: Marker
                    },

                    onReady: () => {
                        new DragDrop(editorRef.current);
                        new Undo({ editor: editorRef.current });
                        
                        // FORZAR que el editor y título sean clickeables
                        setTimeout(() => {
                            // Eliminar/ocultar overlays de Editor.js que bloquean clicks
                            const overlays = document.querySelectorAll('.codex-editor-overlay, .codex-editor-overlay__container, .codex-editor-overlay__rectangle');
                            overlays.forEach(overlay => {
                                overlay.style.display = 'none';
                                overlay.style.pointerEvents = 'none';
                                overlay.style.visibility = 'hidden';
                                overlay.remove(); // Eliminarlos completamente
                            });
                            
                            const titleInput = document.querySelector('.title-input-large');
                            const editorContainer = document.getElementById('editorjs');
                            
                            if (titleInput) {
                                titleInput.style.pointerEvents = 'auto';
                                titleInput.style.zIndex = '100';
                                titleInput.addEventListener('click', function(e) {
                                    e.stopPropagation();
                                    this.focus();
                                }, { capture: true });
                            }
                            
                            if (editorContainer) {
                                editorContainer.style.pointerEvents = 'auto';
                                editorContainer.style.zIndex = '100';
                                
                                // Hacer todos los elementos del editor clickeables
                                const allEditorElements = editorContainer.querySelectorAll('*');
                                allEditorElements.forEach(el => {
                                    el.style.pointerEvents = 'auto';
                                });
                            }
                        }, 500);
                    }
                });
            } catch (error) {
                console.error('Error initializing editor:', error);
            }
        };

        timeoutId = setTimeout(initEditor, 100);

        return () => {
            clearTimeout(timeoutId);
            if (editorRef.current && editorRef.current.destroy) {
                try {
                    editorRef.current.destroy();
                } catch (e) {}
            }
            editorRef.current = null;
        };
    }, []);

    // Cargar categorías y post
    useEffect(() => {
        loadCategories();
        if (id) {
            loadPost();
        }
    }, [id]);

    // FORZAR interactividad después de que todo esté montado
    useEffect(() => {
        const forceInteractivity = () => {
            // CRÍTICO: Eliminar overlays de Editor.js que bloquean clicks
            const overlays = document.querySelectorAll('.codex-editor-overlay, .codex-editor-overlay__container, .codex-editor-overlay__rectangle');
            overlays.forEach(overlay => {
                overlay.style.display = 'none';
                overlay.style.pointerEvents = 'none';
                overlay.style.visibility = 'hidden';
            });
            
            const titleInput = document.querySelector('.title-input-large');
            const editorContainer = document.getElementById('editorjs');
            const allInputs = document.querySelectorAll('.blog-editor-page input, .blog-editor-page select, .blog-editor-page textarea');
            
            // Título
            if (titleInput) {
                titleInput.style.pointerEvents = 'auto';
                titleInput.style.zIndex = '100';
                titleInput.removeAttribute('disabled');
                titleInput.removeAttribute('readonly');
            }
            
            // Editor
            if (editorContainer) {
                editorContainer.style.pointerEvents = 'auto';
                editorContainer.style.zIndex = '100';
                const editorElements = editorContainer.querySelectorAll('*');
                editorElements.forEach(el => {
                    el.style.pointerEvents = 'auto';
                });
            }
            
            // Todos los inputs, selects, textareas
            allInputs.forEach(input => {
                input.style.pointerEvents = 'auto';
                input.removeAttribute('disabled');
            });
        };

        // Ejecutar múltiples veces para asegurar
        forceInteractivity();
        setTimeout(forceInteractivity, 100);
        setTimeout(forceInteractivity, 500);
        setTimeout(forceInteractivity, 1000);

        const interval = setInterval(forceInteractivity, 2000);
        return () => clearInterval(interval);
    }, []);

    const loadPost = async () => {
        try {
            setLoading(true);
            const response = await blogService.getById(id);
            const post = response.data;
            
            let galleryImages = [];
            if (post.gallery_images) {
                if (typeof post.gallery_images === 'string') {
                    try {
                        galleryImages = JSON.parse(post.gallery_images);
                    } catch (e) {
                        console.error('Error parseando gallery_images:', e);
                        galleryImages = [];
                    }
                } else if (Array.isArray(post.gallery_images)) {
                    galleryImages = post.gallery_images;
                }
            }
            
            // Parsear contenido de Editor.js
            let content_es = '';
            if (post.content_es) {
                try {
                    // Verificar si ya es JSON de Editor.js
                    JSON.parse(post.content_es);
                    content_es = post.content_es;
                } catch {
                    // Si no es JSON, crear estructura Editor.js básica
                    content_es = JSON.stringify({
                        time: Date.now(),
                        blocks: [
                            {
                                type: 'paragraph',
                                data: {
                                    text: post.content_es
                                }
                            }
                        ]
                    });
                }
            }
            
            // Parsear scheduled_publish_time si existe
            let scheduled_date = '';
            let scheduled_time = '';
            if (post.scheduled_publish_time) {
                const schedDateTime = new Date(post.scheduled_publish_time);
                scheduled_date = schedDateTime.toISOString().split('T')[0];
                scheduled_time = schedDateTime.toTimeString().slice(0, 5); // HH:MM
            }
            
            setFormData({
                title_es: post.title_es || '',
                slug: post.slug || '',
                content_es: content_es,
                excerpt_es: post.excerpt_es || '',
                category_id: post.category_id || '',
                featured_image: null,
                gallery_images: [],
                existing_gallery_images: galleryImages,
                status: post.status || 'draft',
                published_at: post.published_at ? post.published_at.split('T')[0] : '',
                scheduled_publish_date: scheduled_date,
                scheduled_publish_time: scheduled_time
            });

            if (post.featured_image) {
                setImagePreview(`${MEDIA_URL}${post.featured_image}`);
            }
            
            if (galleryImages.length > 0) {
                const existingPreviews = galleryImages.map(img => `${MEDIA_URL}${img}`);
                setAdditionalImages(existingPreviews);
            } else {
                setAdditionalImages([]);
            }

            // Renderizar contenido en el editor
            if (content_es && editorRef.current) {
                setTimeout(async () => {
                    try {
                        const data = JSON.parse(content_es);
                        console.log('📄 Contenido a cargar en editor:', data);
                        console.log('🔍 Bloques de imagen:', data.blocks?.filter(b => b.type === 'image'));
                        await editorRef.current.render(data);
                    } catch (e) {
                        console.error('Error rendering:', e);
                    }
                }, 300);
            }
        } catch (error) {
            toast.error('Error al cargar el artículo');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoriesService.getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'title_es') {
            const slug = value
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9\s-]/g, '')
                .trim()
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            
            // Solo auto-generar slug si es un nuevo artículo O si el slug actual coincide con el título anterior
            if (!id) {
                setFormData(prev => ({ ...prev, [name]: value, slug }));
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, featured_image: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleAdditionalImages = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({ 
            ...prev, 
            gallery_images: [...prev.gallery_images, ...files] 
        }));
        
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setAdditionalImages(prev => [...prev, ...newPreviews]);
    };

    const removeAdditionalImage = (index) => {
        setFormData(prev => ({
            ...prev,
            gallery_images: prev.gallery_images.filter((_, i) => i !== index)
        }));
        setAdditionalImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!editorRef.current) {
            toast.error('El editor no está listo');
            return;
        }

        // Validaciones de fecha
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        // Validar que no se publique con fecha futura (solo para estado 'published')
        if (formData.status === 'published' && formData.published_at) {
            const publishDate = new Date(formData.published_at);
            if (publishDate > now) {
                toast.error('No puedes publicar con una fecha futura. Usa "Programado" para agendar publicaciones.');
                return;
            }
        }

        // Validar que posts programados tengan fecha y hora futuras
        if (formData.status === 'scheduled') {
            if (!formData.scheduled_publish_date || !formData.scheduled_publish_time) {
                toast.error('Debes especificar fecha y hora para publicaciones programadas');
                return;
            }

            const scheduledDateTime = new Date(`${formData.scheduled_publish_date}T${formData.scheduled_publish_time}`);
            const nowWithTime = new Date();
            
            if (scheduledDateTime <= nowWithTime) {
                toast.error('La fecha y hora programada debe ser futura');
                return;
            }
        }

        try {
            setLoading(true);
            
            // Guardar contenido del editor
            const savedData = await editorRef.current.save();
            
            const data = new FormData();
            data.append('title_es', formData.title_es);
            data.append('slug', formData.slug);
            data.append('content_es', JSON.stringify(savedData));
            data.append('excerpt_es', formData.excerpt_es);
            data.append('category_id', formData.category_id);
            data.append('status', formData.status);
            data.append('published_at', formData.published_at);
            
            // Agregar campos de programación si es scheduled
            if (formData.status === 'scheduled') {
                const scheduledDateTime = `${formData.scheduled_publish_date} ${formData.scheduled_publish_time}`;
                data.append('scheduled_publish_time', scheduledDateTime);
            }

            if (formData.featured_image) {
                data.append('featured_image', formData.featured_image);
            }

            formData.gallery_images.forEach(img => {
                data.append('gallery_images', img);
            });

            if (id) {
                await blogService.update(id, data);
                toast.success('Artículo actualizado exitosamente');
            } else {
                await blogService.create(data);
                toast.success('Artículo creado exitosamente');
            }

            navigate('/blog');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar el artículo');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando...</p>
            </div>
        );
    }

    return (
        <div className="blog-editor-page">
            {/* Topbar */}
            <div className="editor-topbar">
                <button 
                    type="button"
                    onClick={() => navigate('/blog')}
                    className="back-button"
                >
                    ← Volver
                </button>
                
                <h2 className="topbar-title">
                    {id ? 'Editar Artículo' : 'Nuevo Artículo'}
                </h2>
                
                <div className="topbar-actions">
                    <button 
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                        className="btn-secondary-small"
                        disabled={loading}
                    >
                        Guardar borrador
                    </button>
                    <button 
                        type="submit"
                        form="blog-editor-form"
                        className="btn-primary-small"
                        disabled={loading}
                    >
                        {loading ? 'Guardando...' : id ? 'Actualizar' : 'Publicar'}
                    </button>
                </div>
            </div>

            {/* Layout principal */}
            <form id="blog-editor-form" onSubmit={handleSubmit} className="wordpress-layout">
                {/* Área del editor */}
                <div className="editor-area">
                    <input
                        type="text"
                        name="title_es"
                        className="title-input-large"
                        value={formData.title_es}
                        onChange={handleInputChange}
                        onClick={(e) => {
                            console.log('Title clicked!', e.target);
                            e.target.focus();
                        }}
                        onFocus={() => console.log('Title focused!')}
                        placeholder="Escribe el título aquí..."
                        required
                        autoComplete="off"
                        style={{ pointerEvents: 'auto', zIndex: 50 }}
                    />
                    
                    {!id && formData.slug && (
                        <div className="url-preview">
                            URL: <strong className="text-primary-color">/{formData.slug}</strong>
                        </div>
                    )}
                    
                    {/* Editor.js Container */}
                    <div 
                        id="editorjs" 
                        className="editorjs-container"
                        style={{ pointerEvents: 'auto', zIndex: 50 }}
                        onClick={(e) => {
                            console.log('Editor clicked!', e.target);
                        }}
                    ></div>
                    
                    {/* Hint para el usuario */}
                    <div className="editor-hints">
                        <p>
                            <HiOutlineLightBulb style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                            <strong>Consejos:</strong>
                        </p>
                        <ul>
                            <li>Presiona <kbd>/</kbd> para ver todos los bloques disponibles</li>
                            <li>Arrastra el icono <strong>⋮⋮</strong> para mover bloques</li>
                            <li>Usa <kbd>Tab</kbd> para sangría en listas</li>
                            <li>Presiona <kbd>Ctrl+Z</kbd> para deshacer</li>
                            <li>Haz clic en <strong>+</strong> para agregar un nuevo bloque</li>
                        </ul>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="settings-sidebar">
                    <div className="sidebar-panel">
                        <h3 className="panel-title">Publicación</h3>
                        <div className="panel-content">
                            <div className="form-group-compact">
                                <label className="label-compact">Estado</label>
                                <select
                                    name="status"
                                    className="input-compact"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="draft">Borrador</option>
                                    <option value="published">Publicado</option>
                                    <option value="scheduled">Programado</option>
                                    <option value="archived">Archivado</option>
                                </select>
                            </div>

                            {formData.status === 'scheduled' ? (
                                <>
                                    <div className="form-group-compact">
                                        <label className="label-compact">Fecha de Programación *</label>
                                        <input
                                            type="date"
                                            name="scheduled_publish_date"
                                            className="input-compact"
                                            value={formData.scheduled_publish_date}
                                            onChange={handleInputChange}
                                            min={new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                    </div>
                                    <div className="form-group-compact">
                                        <label className="label-compact">Hora de Programación *</label>
                                        <input
                                            type="time"
                                            name="scheduled_publish_time"
                                            className="input-compact"
                                            value={formData.scheduled_publish_time}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <small className="help-text">
                                            La publicación se realizará automáticamente
                                        </small>
                                    </div>
                                </>
                            ) : (
                                <div className="form-group-compact">
                                    <label className="label-compact">Fecha de Publicación</label>
                                    <input
                                        type="date"
                                        name="published_at"
                                        className="input-compact"
                                        value={formData.published_at}
                                        onChange={handleInputChange}
                                        max={formData.status === 'published' ? new Date().toISOString().split('T')[0] : undefined}
                                    />
                                    {formData.status === 'published' && (
                                        <small className="help-text">
                                            No se permiten fechas futuras para publicaciones inmediatas
                                        </small>
                                    )}
                                </div>
                            )}

                            <div className="form-group-compact">
                                <label className="label-compact">Categoría</label>
                                <select
                                    name="category_id"
                                    className="input-compact"
                                    value={formData.category_id}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Seleccionar categoría</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name_es}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group-compact">
                                <label className="label-compact">Slug (URL)</label>
                                <input
                                    type="text"
                                    name="slug"
                                    className={!id ? "input-compact" : "input-compact input-readonly"}
                                    value={formData.slug}
                                    onChange={handleInputChange}
                                    required
                                    readOnly={!id ? false : true}
                                />
                                {!id && (
                                    <small className="help-text">
                                        Se genera automáticamente desde el título
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-panel">
                        <h3 className="panel-title">Imagen Destacada</h3>
                        <div className="panel-content">
                            <div className="image-upload-area">
                                <input
                                    type="file"
                                    id="featured-image"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="featured-image" className="upload-label">
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="image-preview-large" />
                                    ) : (
                                        <div className="upload-placeholder">
                                            <HiOutlineCamera className="upload-icon" />
                                            <span>Seleccionar imagen</span>
                                        </div>
                                    )}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-panel">
                        <h3 className="panel-title">Extracto</h3>
                        <div className="panel-content">
                            <textarea
                                name="excerpt_es"
                                className="textarea-compact"
                                value={formData.excerpt_es}
                                onChange={handleInputChange}
                                placeholder="Breve descripción del artículo..."
                                rows="4"
                            />
                        </div>
                    </div>

                    <div className="sidebar-panel">
                        <h3 className="panel-title">Galería de Imágenes</h3>
                        <div className="panel-content">
                            <input
                                type="file"
                                id="gallery-images"
                                accept="image/*"
                                multiple
                                onChange={handleAdditionalImages}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="gallery-images" className="btn-secondary-small" style={{width: '100%', textAlign: 'center'}}>
                                + Agregar imágenes
                            </label>
                            
                            {additionalImages.length > 0 && (
                                <div className="gallery-grid-compact">
                                    {additionalImages.map((img, index) => (
                                        <div key={index} className="gallery-thumb">
                                            <img src={img} alt={`Gallery ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-thumb"
                                                onClick={() => removeAdditionalImage(index)}
                                            >
                                                <svg viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default BlogEditorJS;
